import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "../services/authApi";
import type { User, AuthContextType, LoginResponse } from "../types/auth";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}): JSX.Element => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Clear auth tokens from localStorage (httpOnly cookies cleared by backend)
  const clearTokens = (): void => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  };

  /**
   * Clear ALL browser storage (comprehensive cleanup for logout)
   *
   * This ensures complete cleanup on logout to prevent stale data:
   * - localStorage: All app data including tokens, user preferences, cached data
   * - sessionStorage: All temporary session data
   * - In-memory state: User object cleared
   *
   * Why comprehensive clearing is critical:
   * 1. Security: Prevents next user from accessing previous user's data
   * 2. State consistency: Eliminates stale cached data causing bugs
   * 3. Privacy: Removes all traces of user activity
   * 4. Clean slate: Ensures fresh login has no leftover state
   */
  const clearAllBrowserData = (): void => {
    try {
      // Clear ALL localStorage (tokens, cached data, user preferences, etc.)
      localStorage.clear();

      // Clear ALL sessionStorage (temporary session data)
      sessionStorage.clear();
    } catch (error) {
      console.error("[LOGOUT] Error clearing browser data:", error);
      // Fallback: try to at least clear tokens individually
      clearTokens();
    }
  };

  /**
   * Login with email and password (tokens stored in httpOnly cookies by backend)
   */
  const login = async (email: string, password: string): Promise<void> => {
    try {
      const response: LoginResponse = await authApi.login(email, password);
      setUser(response.user);

      if (response.user.must_change_password) {
        navigate("/must-reset-password");
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  /**
   * Logout - revoke tokens and clear ALL browser state
   */
  const logout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch (apiError) {
      console.error("[LOGOUT] Backend logout failed:", apiError);
    } finally {
      clearAllBrowserData();
      setUser(null);
      navigate("/login");
      setTimeout(() => window.location.reload(), 100);
    }
  };

  /**
   * Refresh access token (backend reads refresh token from httpOnly cookie)
   */
  const refreshToken = useCallback(async (): Promise<void> => {
    try {
      const response = await authApi.refresh();
      setUser(response.user);
    } catch (error) {
      console.error("Token refresh failed:", error);
      clearTokens();
      setUser(null);
      navigate("/login");
    }
  }, [navigate]);

  /**
   * Check if user has a specific permission.
   * Permissions are loaded from auth_user_permissions_mv via /api/auth/me.
   */
  const hasPermission = (permission: string): boolean => {
    if (user === null) return false;

    // Admin has all permissions (wildcard)
    if (user.roles?.includes("admin")) return true;

    // Check actual permissions from backend
    // Use Array.isArray for type safety with optional permissions array
    return Array.isArray(user.permissions) && user.permissions.includes(permission);
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) ?? false;
  };

  /**
   * Get user's preferred timezone for date/time display conversion.
   * Returns the user's configured timezone or defaults to America/Chicago (Central Time).
   *
   * All dates are stored as UTC in the database. This timezone is used
   * exclusively for frontend display conversion via date-fns-tz.
   */
  const getUserTimezone = (): string => {
    const timezone: string = user?.timezone ?? "America/Chicago";
    return timezone;
  };

  /**
   * Load current user on component mount (auth via httpOnly cookie)
   */
  useEffect(() => {
    const loadUser = async (): Promise<void> => {
      try {
        const currentUser = await authApi.getCurrentUser();
        setUser(currentUser);
      } catch {
        // No valid session - user needs to login
        clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  /**
   * Auto-refresh token before expiry
   * Access tokens expire in 30 minutes, refresh 5 minutes before
   */
  useEffect(() => {
    if (user === null) return;

    // Refresh 5 minutes before expiry (access token = 30min)
    const refreshInterval = setInterval(
      () => {
        void refreshToken();
      },
      25 * 60 * 1000
    ); // 25 minutes

    return () => clearInterval(refreshInterval);
  }, [user, refreshToken]);

  const value: AuthContextType = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    login,
    logout,
    refreshToken,
    hasPermission,
    hasRole,
    getUserTimezone,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
